import { Component, OnInit } from '@angular/core';
import { LoggerService } from 'src/app/services/logger/logger.service';
import { ApiCalendar, CalendarEventTypesApi, SlideDirections } from 'src/app/services/rest-api/rest-api';
import { RestApiService } from 'src/app/services/rest-api/rest-api.service';
import { ToastNotificationService } from 'src/app/services/toast-notification/toast-notification.service';
import * as dayjs from 'dayjs';
import * as utc from 'dayjs/plugin/utc';
import { CalendarComponentInput } from 'src/app/shared/calendar/calendar-input';
import { SelectItem } from 'src/app/shared/dropdown/dropdown';
import { AbstractControl, UntypedFormControl } from '@angular/forms';

@Component({
  templateUrl: './business-calendar.component.html',
  styleUrls: ['./business-calendar.component.scss'],
  standalone: false
})
export class BusinessCalendarComponent implements OnInit {
  public preloader = true;
  public apiError = false;

  private apiCalendarData!: ApiCalendar;
  private selectedDates: dayjs.Dayjs[] = [];

  public slideDirections = SlideDirections;

  public selectedMonth = NaN;
  public selectedYear = NaN;

  public selectedMonthControl!: AbstractControl;
  public selectedYearControl!: AbstractControl;

  public availableMonths: SelectItem[] = [
    {
      label: 'calendar.months.january',
      value: 0,
      translate: true,
    },
    {
      label: 'calendar.months.february',
      value: 1,
      translate: true,
    },
    {
      label: 'calendar.months.march',
      value: 2,
      translate: true,
    },
    {
      label: 'calendar.months.april',
      value: 3,
      translate: true,
    },
    {
      label: 'calendar.months.may',
      value: 4,
      translate: true,
    },
    {
      label: 'calendar.months.june',
      value: 5,
      translate: true,
    },
    {
      label: 'calendar.months.july',
      value: 6,
      translate: true,
    },
    {
      label: 'calendar.months.august',
      value: 7,
      translate: true,
    },
    {
      label: 'calendar.months.september',
      value: 8,
      translate: true,
    },
    {
      label: 'calendar.months.october',
      value: 9,
      translate: true,
    },
    {
      label: 'calendar.months.november',
      value: 10,
      translate: true,
    },
    {
      label: 'calendar.months.december',
      value: 11,
      translate: true,
    },
  ];

  public availableYears: SelectItem[] = [];

  private get selectedDate(): dayjs.Dayjs {
    return dayjs().utcOffset(0).month(this.selectedMonth).year(this.selectedYear);
  }

  public get selectedCalendarData(): CalendarComponentInput[] {
    const dates: CalendarComponentInput[] = [];

    for (const calendarDate of this.selectedDates) {
      dates.push({
        day: calendarDate,
        isCzechHoliday: this.isDateException(calendarDate, 'CZ_PUBLIC_HOLIDAY'),
        isUkWalesBankHoliday: this.isDateException(calendarDate, 'UK_PUBLIC_HOLIDAY'),
        isUsBankHoliday: this.isDateException(calendarDate, 'US_PUBLIC_HOLIDAY'),
        isTradeDay: this.isDateException(calendarDate, 'TRADE_DAY'),
        isEstateTradeDay: this.isDateException(calendarDate, 'ESTATE_TRADE_DAY'),
      });
    }

    return dates;
  }

  public constructor(
    private restApiService: RestApiService,
    public toastService: ToastNotificationService,
    private loggerService: LoggerService,
  ) {
    dayjs.extend(utc);

    this.selectedMonth = dayjs().utcOffset(0).month();
    this.selectedYear = dayjs().utcOffset(0).year();

    this.selectedMonthControl = new UntypedFormControl(this.selectedMonth);
    this.selectedYearControl = new UntypedFormControl(this.selectedYear);

    this.generateAvailableYears();
  }

  private generateAvailableYears(): void {

    for (let index = 0; index < 2; index++) {
      const year = dayjs().utcOffset(0).get('year');
      this.availableYears.push({
        label: year + index + '',
        value: year + index,
      });
    }
  }

  public ngOnInit(): void {
    this.updateData();
  }

  private updateFormControl(): void {
    this.selectedMonthControl.patchValue(this.selectedMonth);
    this.selectedYearControl.patchValue(this.selectedYear);
  }

  private updateData(): void {
    this.preloader = true;
    this.updateFormControl();
    this.generateMonth();

    this.restApiService.getCalendar(
      this.selectedDates[0],
      this.selectedDates[this.selectedDates.length - 1],
    ).subscribe({
      error: this.handleCalendarError.bind(this),
      next: this.handleCalendarData.bind(this),
    });
  }

  private isDateException(calendarDate: dayjs.Dayjs, type: keyof typeof CalendarEventTypesApi | 'TRADE_DAY' | 'ESTATE_TRADE_DAY'): boolean {
    if(type === 'TRADE_DAY') {
      return !!this.apiCalendarData.rebalancingDates.find(date => calendarDate.isSame(dayjs.utc(date.runningDate), 'day'));
    } else if (type === 'ESTATE_TRADE_DAY') {
      return !!this.apiCalendarData.rebalancingDates.find(date => calendarDate.isSame(dayjs.utc(date.runningDate), 'day'))?.rebalancedMonthly;
    } else if (type === 'CZ_PUBLIC_HOLIDAY' || type === 'UK_PUBLIC_HOLIDAY' || type === 'US_PUBLIC_HOLIDAY') {
      const typeDates = this.apiCalendarData.calendarEvents.filter(event => event.calendarEventType === type);
      return !!typeDates.find(date => calendarDate.isSame(dayjs.utc(date.eventDate), 'day'));
    } else {
      this.loggerService.error('Unknown type for Calendar date: ' + type);
      return false;
    }
  }

  private generateMonth(): void {
    this.selectedDates.length = 0;

    const startDate = this.selectedDate;

    const firstDayOfSelectedMonth = startDate.startOf('month');
    const lastDayOfSelectedMonth = startDate.endOf('month');

    for (let i = 0; i < lastDayOfSelectedMonth.date(); i++) {
      this.selectedDates.push(firstDayOfSelectedMonth.add(i, 'day'));
    }
  }

  private handleCalendarError(error: unknown): void {
    this.apiError = true;
    this.preloader = false;
    this.loggerService.error(typeof error === 'function' ? error() : error);

    this.toastService.unknownApiError();
  }

  private handleCalendarData(data: ApiCalendar): void {
    this.apiCalendarData = data;
    this.preloader = false;
    this.apiError = false;
  }

  public valueUpdated(newValue: SelectItem | null, type: 'month' | 'year'): void {
    if(type === 'month') {
      this.selectedMonth = newValue!.value as number;
    }
    if(type === 'year') {
      this.selectedYear = newValue!.value as number;
    }

    this.preloader = true;
    this.updateData();
  }

  public setNext(type: 'month' | 'year'): void {
    if(type === 'month') {
      this.selectedMonth += 1;

      if(this.selectedMonth === 12) {
        this.selectedMonth = 0;
        this.selectedYear += 1;
      }
    }
    if(type === 'year') {
      this.selectedYear += 1;
    }

    this.updateData();
  }

  public setPrev(type: 'month' | 'year'): void {
    if(type === 'month') {
      this.selectedMonth -= 1;

      if(this.selectedMonth === -1) {
        this.selectedMonth = 11;
        this.selectedYear -= 1;
      }
    }
    if(type === 'year') {
      this.selectedYear -= 1;
    }

    this.updateData();
  }

  public arrowDisabled(type: 'month' | 'year', direction: SlideDirections): boolean {
    if(type === 'month') {
      if(direction === SlideDirections.NEXT) {
        const selectedYearIndex = this.availableYears.findIndex(item => item.value === this.selectedYear);
        return (selectedYearIndex === this.availableYears.length - 1) && (this.selectedMonth === 11);
      }
      if(direction === SlideDirections.PREV) {
        const selectedYearIndex = this.availableYears.findIndex(item => item.value === this.selectedYear);
        return (selectedYearIndex === 0) && (this.selectedMonth === 0);
      }
    }

    if(type === 'year') {
      if(direction === SlideDirections.NEXT) {
        return this.availableYears.findIndex(item => item.value === this.selectedYear) === this.availableYears.length;
      }
      if(direction === SlideDirections.PREV) {
        return this.availableYears.findIndex(item => item.value === this.selectedYear) === 0;
      }
    }

    return false;
  }
}
